import { action, computed, observable, runInAction } from 'mobx';
import { FAKE_PICTURE_ID, MIN_VOTES_TO_SHOW_SCORE_BUTTON } from '../constants';
import { trackEvent } from '../lib/tracking';
import { AbstractModel } from './AbstractModel';
import { Pair, Picture, PicturesBatch, UnlockVotesResult, VoteBody } from './types';

export class VotesModel extends AbstractModel {
  @observable
  votesQueue: Pair[] = [];

  // Array of all voted Pairs in current session
  private sentVotes: Pair[] = [];
  private preloadedPairs: Pair[] = [];
  private pendingImageFetchPromise?: Promise<any>;
  private initialVotesPromise?: Promise<Pair[]>;
  private fetchBatchPromise?: Promise<void>;

  /** If true, the app will not try to fetch other g */
  @observable
  backendOutOfPics: boolean = false;

  @computed
  get votePair() {
    return this.votesQueue.length ? this.votesQueue[0] : undefined;
  }

  @action
  private addBatch(batch: PicturesBatch) {
    const newPairs = batch.pairs.filter((batchPair) => {
      const pairsContainSamePicturesPartial = pairsContainSamePictures(batchPair);
      // filter out votes that are already in queue
      if (this.votesQueue.some(pairsContainSamePicturesPartial)) {
        return false;
      }
      // filter out votes that have beed already voted in current session
      if (this.sentVotes.some(pairsContainSamePicturesPartial)) {
        return false;
      }
      return true;
    });
    this.votesQueue = this.votesQueue.concat(newPairs);
  }

  @action
  private removePair(leftPictureId: string, rightPictureId: string) {
    const pairIndexToRemove = this.votesQueue.findIndex(
      ({ leftPicture, rightPicture }) => leftPicture.id === leftPictureId && rightPicture.id === rightPictureId
    );

    if (pairIndexToRemove > -1) {
      const removedPair = this.votesQueue[pairIndexToRemove];
      console.log(removedPair);
      this.votesQueue.splice(pairIndexToRemove, 1);
      this.sentVotes.push(removedPair);
    }
  }

  @action
  async fetchBatch() {
    try {
      const data: PicturesBatch = await this.rootStore.firebase.getBatch();
      runInAction(() => {
        this.backendOutOfPics = !data || !data.pairs || !data.pairs.length;
        this.addBatch(data);
        if (this.backendOutOfPics) {
          trackEvent('nothing_to_vote_on');
        }
      });
    } catch (error) {
      throw error;
    }
  }

  @action
  async fetchInitialVotePairs() {
    if (this.rootStore.onboarding.sawInitialVotePairs || this.initialVotesPromise) return;

    try {
      this.initialVotesPromise = this.rootStore.firebase.getInitialVotePairs(MIN_VOTES_TO_SHOW_SCORE_BUTTON);
      const initialPairs = await this.initialVotesPromise;
      runInAction(() => {
        // Only use initial pairs if it takes longer to fetch first batch to reduce users waiting
        if (this.votesQueue.length > 0) return;
        // Map initial votes id onto vote pairs
        const pairsWithIds = initialPairs.map((pair) => {
          const newPair = { ...pair };
          newPair.leftPicture.id = newPair.leftPicture.id ?? FAKE_PICTURE_ID;
          newPair.rightPicture.id = newPair.rightPicture.id ?? FAKE_PICTURE_ID;
          return newPair;
        });

        this.votesQueue.push(...pairsWithIds);
      });
    } catch (error) {
      // TODO: improve error handling
      console.error('Failed to fetch initial votes', error);
    }
  }

  @action
  async sendVote(payload: VoteBody) {
    const { user } = this.rootStore.currentUser;
    if (!user) {
      // Sometime, for some reason, the user is not loaded 🤷‍♂️
      return;
    }

    try {
      trackEvent('vote');
      this.removePair(payload.leftPictureId, payload.rightPictureId);
      // Optimistic update of user's coins
      user.coins++;
      user.votesGiven++;

      await this.rootStore.firebase.sendVote(payload);

      await this.ensureVotes();
    } catch (error) {
      // Dev note: To ensure Mobx working correctly, all @observable modifications after the first await in async functions have to be wrapped in `runInAction` callback
      runInAction(() => {
        // Undo optimistic update
        user.coins--;
        user.votesGiven--;
      });

      // TODO: improve error handling
      console.error('Vote failed', error);
    }
  }

  @action
  async ensureVotes() {
    if (this.votesQueue.length < 3 && !this.backendOutOfPics && !this.fetchBatchPromise) {
      this.fetchBatchPromise = this.fetchBatch();
      await this.fetchBatchPromise;
      this.fetchBatchPromise = undefined;
    }
    this.preloadVoteImages();
  }

  @action
  async unlockAllUserVotes(): Promise<UnlockVotesResult> {
    try {
      trackEvent('unlock_all_user_votes');
      const result = await this.rootStore.firebase.unlockAllUserVotes();
      // Update user's coins locally on FE as coins aren't synced real-time
      runInAction(() => (this.rootStore.currentUser.user!.coins -= result.coinsSpend));
      return result;
    } catch (error) {
      throw error;
    }
  }

  /** Prefetch votes images in the background to improve delivery speed during voting*/
  private async preloadVoteImages() {
    if (this.pendingImageFetchPromise) {
      return;
    }

    const pendingVote = this.votesQueue.find((vote) => {
      return !this.preloadedPairs.includes(vote);
    });

    if (pendingVote) {
      this.preloadedPairs.push(pendingVote);
      this.pendingImageFetchPromise = Promise.all([
        this.getImgFetchPromise(pendingVote.leftPicture),
        this.getImgFetchPromise(pendingVote.rightPicture),
      ]);
      await this.pendingImageFetchPromise;
      this.pendingImageFetchPromise = undefined;
      this.preloadVoteImages();
    }
  }

  private getImgFetchPromise(picture: Picture) {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = picture.url;
      img.onload = () => resolve();
      // TODO: maybe somehow handle error
      img.onerror = () => resolve();
    });
  }
}

const pairsContainSamePictures = (pair1: Pair) => (pair2: Pair) =>
  pair1.leftPicture.id === pair2.leftPicture.id || pair1.leftPicture.id === pair2.rightPicture.id;
